/**
 * Presentation for POS in-app notification types.
 *
 * `notif_type` is a free varchar in public.notifications on purpose — a new producer can raise
 * a new type without a back-end release. These maps only decorate the known ones; anything
 * unrecognised falls back to DEFAULT_NOTIF_* so an unmapped type still renders correctly.
 *
 * store-service raises approval bells through one producer wired into the approval engine
 * (`notification.services.ApprovalNotifier`), so every document in the procurement chain emits
 * `<DOC_TYPE>_REQUEST` to approvers and `<DOC_TYPE>_DECISION` to the applicant. Adding a doc type
 * to the engine means adding its two entries here too, or the bell shows a bare SCREAMING_SNAKE
 * string where a label should be.
 */

export const NOTIF_TYPE_LABEL: {[key: string]: string} = {
  ANNOUNCEMENT: 'Announcement',
  ALERT: 'Alert',
  REMINDER: 'Reminder',
  APPROVAL: 'Approval',

  // Approval-chain types, raised by store-service's ApprovalNotifier.
  INTERNAL_REQUISITION_REQUEST: 'Internal Requisition — approval needed',
  SPR_REQUEST: 'Store Purchase Req. — approval needed',
  PURCHASE_ORDER_REQUEST: 'Purchase Order — approval needed',
  CHALLAN_REQUEST: 'Challan — approval needed',
  QUALITY_CERTIFICATE_REQUEST: 'Quality Certificate — approval needed',
  MRR_REQUEST: 'MRR — approval needed',
  ISSUE_REQUEST: 'Issue — approval needed',
  DAMAGE_REQUEST: 'Damage — approval needed',
  GATE_PASS_REQUEST: 'Gate Pass — approval needed',
  ADJUSTMENT_REQUEST: 'Stock Adjustment — approval needed',
  INTERNAL_REQUISITION_DECISION: 'Internal Requisition — decided',
  SPR_DECISION: 'Store Purchase Req. — decided',
  PURCHASE_ORDER_DECISION: 'Purchase Order — decided',
  CHALLAN_DECISION: 'Challan — decided',
  QUALITY_CERTIFICATE_DECISION: 'Quality Certificate — decided',
  MRR_DECISION: 'MRR — decided',
  ISSUE_DECISION: 'Issue — decided',
  DAMAGE_DECISION: 'Damage — decided',
  GATE_PASS_DECISION: 'Gate Pass — decided',
  ADJUSTMENT_DECISION: 'Stock Adjustment — decided',
}

export const NOTIF_TYPE_TONE: {[key: string]: string} = {
  ANNOUNCEMENT: 'info',
  ALERT: 'danger',
  REMINDER: 'warning',
  APPROVAL: 'primary',

  INTERNAL_REQUISITION_REQUEST: 'primary',
  SPR_REQUEST: 'primary',
  PURCHASE_ORDER_REQUEST: 'primary',
  CHALLAN_REQUEST: 'primary',
  QUALITY_CERTIFICATE_REQUEST: 'primary',
  MRR_REQUEST: 'primary',
  ISSUE_REQUEST: 'primary',
  DAMAGE_REQUEST: 'primary',
  GATE_PASS_REQUEST: 'primary',
  ADJUSTMENT_REQUEST: 'primary',
  INTERNAL_REQUISITION_DECISION: 'info',
  SPR_DECISION: 'info',
  PURCHASE_ORDER_DECISION: 'info',
  CHALLAN_DECISION: 'info',
  QUALITY_CERTIFICATE_DECISION: 'info',
  MRR_DECISION: 'info',
  ISSUE_DECISION: 'info',
  DAMAGE_DECISION: 'info',
  GATE_PASS_DECISION: 'info',
  ADJUSTMENT_DECISION: 'info',
}

export const NOTIF_TYPE_ICON: {[key: string]: string} = {
  ANNOUNCEMENT: 'campaign',
  ALERT: 'warning',
  REMINDER: 'schedule',
  APPROVAL: 'task_alt',

  INTERNAL_REQUISITION_REQUEST: 'assignment',
  SPR_REQUEST: 'shopping_basket',
  PURCHASE_ORDER_REQUEST: 'shopping_cart',
  CHALLAN_REQUEST: 'local_shipping',
  QUALITY_CERTIFICATE_REQUEST: 'fact_check',
  MRR_REQUEST: 'inventory_2',
  ISSUE_REQUEST: 'outbound',
  DAMAGE_REQUEST: 'report_problem',
  GATE_PASS_REQUEST: 'exit_to_app',
  ADJUSTMENT_REQUEST: 'tune',
  INTERNAL_REQUISITION_DECISION: 'assignment',
  SPR_DECISION: 'shopping_basket',
  PURCHASE_ORDER_DECISION: 'shopping_cart',
  CHALLAN_DECISION: 'local_shipping',
  QUALITY_CERTIFICATE_DECISION: 'fact_check',
  MRR_DECISION: 'inventory_2',
  ISSUE_DECISION: 'outbound',
  DAMAGE_DECISION: 'report_problem',
  GATE_PASS_DECISION: 'exit_to_app',
  ADJUSTMENT_DECISION: 'tune',
}

export const DEFAULT_NOTIF_TONE = 'secondary'
export const DEFAULT_NOTIF_ICON = 'notifications'

/**
 * The filter dropdown deliberately lists only the generic types. The sixteen approval-chain types
 * resolve through NOTIF_TYPE_LABEL for display, but putting them all in a select would bury the
 * handful anyone actually filters by.
 */
const GENERIC_NOTIF_TYPES = ['ANNOUNCEMENT', 'ALERT', 'REMINDER', 'APPROVAL']

export const NOTIF_TYPE_OPTIONS = GENERIC_NOTIF_TYPES.map((id) => ({
  id,
  title: NOTIF_TYPE_LABEL[id],
}))

export const notifTone = (notifType?: string) => NOTIF_TYPE_TONE[notifType || ''] || DEFAULT_NOTIF_TONE
export const notifIcon = (notifType?: string) => NOTIF_TYPE_ICON[notifType || ''] || DEFAULT_NOTIF_ICON
export const notifLabel = (notifType?: string) => NOTIF_TYPE_LABEL[notifType || ''] || notifType || '—'
